/**
 * helper functions for editable ag-grid
 */

import {
  concat,
  forEach,
  get,
  has,
  head,
  includes,
  isEmpty,
  isEqual,
  isNil,
  reduce,
  some,
  stubTrue,
  tail,
} from 'lodash';
import { CellClassParams, GridApi, RowNode } from 'ag-grid-community';

import {
  Primitive,
  XuxGridOptions,
  XUX_GRID_ACTIONS,
} from '@geniuspos/ui-shared';
import { XuxGridComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components';
import { getDisplayedItemsIds } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { XuxGridCustomComponent } from '@components/xux-grid-custom';
import {
  GridRowClass,
  IRowParams,
  IShouldUpdateParams,
  IUpdateRowNodeParams,
} from '@app/shared/types';
import { GRID_ROOT_NODE_ID } from '@config/global-constants';
import { getAllRowsNodeFromGrid } from '@app/shared/utils/common-utils';
import { XUXListContainerComponent } from '@app/containers/base';
import { XuxEditGridCustomComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget-custom.component';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';

export const lookupValue = (
  list,
  currentValue,
  filterKey,
  returnValue,
): any => {
  return get(
    list.find((item) => item[filterKey] === currentValue),
    returnValue,
    null,
  );
};

export const updateValueToNumber = (params, key): boolean => {
  if (params.oldValue !== params.newValue) {
    params.data[key] = parseInt(params.newValue, 10);

    return true;
  }

  // no change, so no refresh needed
  return false;
};

/**
 * update xuxGrid component filter
 *
 * @param xuxGrid target xux grid
 * @param searchFilterString search filter
 */
export const updateGridSortFilter = (
  xuxGrid: XuxGridComponent | XuxGridCustomComponent,
  searchFilterString: string,
): void => {
  xuxGrid.handleInternalActions({
    payload: { value: searchFilterString },
    type: XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED,
  });

  xuxGrid.view.quickFilter = searchFilterString;
};

/**
 * handle grid overlay show or not
 * and return displayItemsIds
 *
 * @param xuxGridComponent target xux grid
 * @returns displayItemsIds
 */
export const handleGridOverlay = (
  xuxGridComponent:
    | XuxGridComponent
    | XuxGridCustomComponent
    | XUXListContainerComponent,
): string[] => {
  if (!xuxGridComponent.gridApi) {
    return [];
  }

  const displayedItemsIds = getDisplayedItemsIds(xuxGridComponent.gridApi);

  if (isEmpty(displayedItemsIds)) {
    xuxGridComponent.gridApi.showNoRowsOverlay();
  } else {
    xuxGridComponent.gridApi.hideOverlay();
  }

  return displayedItemsIds;
};

/**
 * is Target Row in tree view
 *
 * @param params
 * @returns
 */
export const isTargetRow = (params: {
  node: RowNode;
  targetLevel: number;
  context: CellClassParams['context'];
}): boolean => {
  const { context } = params;

  // not tree view mode, just return false
  if (!context?.isTreeViewMode) {
    return false;
  }

  const node = params.node;

  if (has(node, 'level') || has(node, 'uiLevel')) {
    return (
      isEqual(get(node, 'level'), params.targetLevel) ||
      isEqual(get(node, 'uiLevel'), params.targetLevel)
    );
  }

  return false;
};

/**
 * get list grid row cell class rule
 * 'warn-row': is for data change check, if row data was changed by other dependency data, will add this to warn user
 *
 * @returns row cell class rule
 */
export const getListGridRowCellClass = (): Record<
  string,
  (params: CellClassParams) => boolean
> => {
  return {
    'xvi-grid-warn-row': (params): boolean => {
      return get(params, 'data.error');
    },
    [GridRowClass.ERROR_ROW]: (params): boolean => {
      return get(params, 'data.criticalError');
    },
    'major-category-row': (params): boolean => {
      return (
        isTargetRow({
          node: params.node,
          targetLevel: 0,
          context: params.context,
        }) || get(params, 'data.isMajorCategoryRow', false)
      );
    },
    'minor-category-row': (params): boolean => {
      return (
        isTargetRow({
          node: params.node,
          targetLevel: 1,
          context: params.context,
        }) || get(params, 'data.isMinorCategoryRow', false)
      );
    },
    'inventory-item-row': (params): boolean => {
      return (
        isTargetRow({
          node: params.node,
          targetLevel: 2,
          context: params.context,
        }) || get(params, 'data.isLeaf', false)
      );
    },
  };
};

export const getListGridRowCellClassForTransfer = (
  option?: IRowParams,
): Record<string, (params: CellClassParams) => boolean> => {
  return {
    ...getListGridRowCellClass(),
    'xvi-grid-warn-row': (params): boolean => {
      return get(params, 'data.error', []).length > 0;
    },
    'inventory-item-can-duplicate': (params): boolean => {
      return (
        get(option, 'isDuplicate') && get(params, 'data.is_duplicated', false)
      );
    },
  };
};

/**
 * Check if the row node is a root node
 *
 * There are three conditions to determine if a row node is a root node:
 * 1. The level of the row node is less than 0.
 * 2. The row node does not have a 'uiLevel' property.
 * 3. The id of the row node is equal to GRID_ROOT_NODE_ID.
 *
 * @param rowNode
 * @returns
 */
export const isRootRowNode = (rowNode: RowNode): boolean => {
  return (
    get(rowNode, 'level') < 0 ||
    !has(rowNode, 'uiLevel') ||
    isEqual(get(rowNode, 'id'), GRID_ROOT_NODE_ID)
  );
};

/**
 * get all parent nodes from given row nodes
 *
 * @param rowNodes
 * @returns
 */
export const getAllParentNodes = (rowNodes: RowNode[]): RowNode[] => {
  return reduce(
    rowNodes,
    (parentNodes: RowNode[], rowNode: RowNode) => {
      const parentNode = rowNode.parent;

      if (
        parentNode === null ||
        isRootRowNode(parentNode) ||
        includes(parentNodes, parentNode)
      ) {
        return parentNodes;
      }

      return [...parentNodes, parentNode];
    },
    [],
  );
};

export const updateSelfAndParentNode = (rowNode: RowNode, field: string) => {
  if (!rowNode || isRootRowNode(rowNode)) {
    return;
  }

  const children = rowNode.childrenAfterFilter;

  if (isEmpty(children)) {
    return;
  }

  const firstChild = children[0];
  const firstChildValue = get(firstChild, ['data', field]);

  const isSomeChildNotSame = some(tail(children), (child) => {
    return !isEqual(get(child, ['data', field]), firstChildValue);
  });

  const newValue = isSomeChildNotSame ? null : firstChildValue;

  // Set parent node sync field to false
  rowNode.setDataValue(field, newValue);
};

export const updateNodesParentsAfterFiltered = (
  rowNodes: RowNode[],
  options: {
    gridApi: GridApi;
    field: string;
    refreshGrid: boolean;
    newParentNodes?: RowNode[];
  },
): void => {
  const { gridApi, refreshGrid, newParentNodes = [] } = options;

  if (!gridApi) {
    return;
  }

  const parentNodes: RowNode[] = getAllParentNodes(rowNodes);

  if (isEmpty(parentNodes)) {
    if (refreshGrid && !isEmpty(newParentNodes)) {
      gridApi.refreshCells({
        rowNodes: newParentNodes,
        force: true,
      });
    }

    return;
  }

  forEach(parentNodes, (parentNode) => {
    updateSelfAndParentNode(parentNode, options.field);
  });

  // also update one more level parent nodes
  updateNodesParentsAfterFiltered(parentNodes, {
    ...options,
    newParentNodes: concat(get(options, 'newParentNodes', []), parentNodes),
  });
};

/**
 * update parent nodes after filter changed
 *
 * @param params
 * @returns
 */
export const updateRowNodesAfterFiltered = (params: {
  gridApi: GridApi;
  gridOptions: XuxGridOptions;
  field: string;
  refreshGrid?: boolean;
}): void => {
  const { gridApi, gridOptions, field, refreshGrid = true } = params;

  if (!gridApi || !gridOptions.treeViewMode) {
    return;
  }

  const allDisplayedRows: RowNode[] = getAllRowsNodeFromGrid(gridApi, true);

  if (isEmpty(allDisplayedRows)) {
    return;
  }

  updateNodesParentsAfterFiltered(allDisplayedRows, {
    gridApi,
    field,
    refreshGrid,
    newParentNodes: [],
  });
};

/**
 * is some child node value not same as given row value
 * will return true if some child node value is different
 * will use first child node value as compare value when row value is nil
 *
 * @param params
 * @returns
 */
export const getIsSomeChildNotSame = (params: {
  rowNode: RowNode;
  rowField: string;
  rowValue: Primitive;
}): boolean => {
  const { rowNode, rowField, rowValue } = params;

  const children = rowNode.childrenAfterFilter;

  if (isEmpty(children)) {
    return false;
  }

  const compareValue = isNil(rowValue)
    ? get(head(children), ['data', rowField])
    : rowValue;

  return some(children, (child) => {
    const childValue = get(child, ['data', rowField]);

    const curLevelNotSame =
      !isEqual(child, rowNode) &&
      !isEqual(childValue?.toString(), compareValue?.toString());

    return (
      curLevelNotSame ||
      getIsSomeChildNotSame({
        rowNode: child,
        rowField,
        rowValue: null,
      })
    );
  });
};

export const updateParentNodeAfterFiltered = (
  rowNode: RowNode,
  params: {
    gridApi: GridApi;
    rowValue: Primitive;
    rowField: string;
    recursively?: boolean;
    refreshGrid?: boolean;
  },
) => {
  const {
    gridApi,
    rowField,
    rowValue,
    recursively = true,
    refreshGrid = true,
  } = params;

  if (!gridApi) {
    return;
  }

  const parentNode = rowNode.parent;

  if (!parentNode || isRootRowNode(parentNode)) {
    return;
  }

  const newValue = getIsSomeChildNotSame({
    rowNode: parentNode,
    rowField,
    rowValue,
  })
    ? null
    : rowValue;

  // Set parent node sync field to false
  parentNode.setDataValue(rowField, newValue);

  if (refreshGrid) {
    // Redraw row when newValue is nil to avoid wrong value/placeholder display
    if (isNil(newValue)) {
      gridApi.redrawRows({ rowNodes: [parentNode] });
    } else {
      gridApi.refreshCells({
        rowNodes: [parentNode],
      });
    }
  }

  // Update next parent node recursively
  if (recursively) {
    updateParentNodeAfterFiltered(parentNode, {
      ...params,
      rowValue: newValue,
    });
  }
};

export const updateChildNodeAfterFiltered = (
  rowNode: RowNode,
  params: {
    gridApi: GridApi;
    rowValue: Primitive;
    rowField: string;
    recursively?: boolean;
    refreshGrid?: boolean;
  },
) => {
  const {
    gridApi,
    rowField,
    rowValue,
    recursively = true,
    refreshGrid = true,
  } = params;

  if (!gridApi) {
    return;
  }
  const children = rowNode.childrenAfterFilter;

  if (isEmpty(children)) {
    return;
  }

  forEach(children, (childNode) => {
    childNode.setDataValue(rowField, rowValue);

    // Update next child nodes recursively
    if (recursively) {
      updateChildNodeAfterFiltered(childNode, params);
    }
  });

  if (refreshGrid) {
    gridApi.refreshCells({ rowNodes: children });
  }
};

export const updateNestedNodesAfterFiltered = (
  rowNode: RowNode,
  params: {
    gridApi: GridApi;
    rowValue: Primitive;
    rowField: string;
    recursively?: boolean;
    refreshGrid?: boolean;
    shouldUpdate?: (shouldUpdateParams: {
      node: RowNode;
      rowField: string;
      rowValue: Primitive;
      gridApi: GridApi;
      direction: 'parent' | 'child';
    }) => boolean;
  },
): boolean => {
  const { gridApi, rowField, rowValue, shouldUpdate = stubTrue } = params;

  if (!gridApi) {
    return false;
  }

  let selfUpdated = false;

  // Update current row node value when it's different to avoid unnecessary grid refresh and applying wrong value
  if (!isEqual(rowValue, get(rowNode, ['data', rowField]))) {
    rowNode.setDataValue(rowField, rowValue);

    selfUpdated = true;
  }

  const shouldUpdateParams = {
    node: rowNode,
    rowField,
    rowValue,
    gridApi,
  };

  // we think we have updated child nodes is updated.
  let childrenUpdated = false;

  if (shouldUpdate({ ...shouldUpdateParams, direction: 'child' })) {
    childrenUpdated = true;
    //we need try to update all its children nodes values too
    updateChildNodeAfterFiltered(rowNode, params);
  }

  // update parent after child to make sure the parent node value is correct based on the latest child nodes value
  if (shouldUpdate({ ...shouldUpdateParams, direction: 'parent' })) {
    //we need try to update all its parent nodes values too
    updateParentNodeAfterFiltered(rowNode, params);
  }

  return selfUpdated || childrenUpdated;
};

/**
 * Will expand or collapse all grid nodes
 * Only expand/collapse nodes from given maxLevel and below when maxLevel is provided
 *
 * @param params
 * @returns
 */
export const expandOrCollapseGrid = (params: {
  gridApi: GridApi;
  expanded: boolean;
  maxLevel?: number;
  overLevelAlwaysExpanded?: boolean;
}): void => {
  const {
    gridApi,
    expanded,
    maxLevel,
    overLevelAlwaysExpanded = true,
  } = params;

  if (!gridApi) {
    return;
  }

  if (!isNil(maxLevel)) {
    gridApi.forEachNode((rowNode) => {
      if (rowNode.level >= maxLevel) {
        rowNode.setExpanded(expanded);
      } else if (overLevelAlwaysExpanded) {
        rowNode.setExpanded(true);
      }
    });

    return;
  }

  if (expanded) {
    gridApi.expandAll();
  } else {
    gridApi.collapseAll();
  }
};

/**
 * Determines whether to update child nodes based on non-empty row values.
 * Always updates parent nodes.
 * Only updates child nodes that have non-empty row values.
 *
 * @param params
 * @returns
 */
export const shouldUpdateChildWhenNotEmpty: IUpdateRowNodeParams['shouldUpdate'] =
  (params: IShouldUpdateParams) => {
    if (isEqual(params.direction, 'parent')) {
      //always update parent nodes
      return true;
    }

    // for child nodes, only update nodes with non-empty rowValue
    return isEqual(params.direction, 'child') && !isEmpty(params.rowValue);
  };

/**
 * Updates the grid row height based on the name wrap setting.
 *
 * @param options
 *
 *
 * @returns
 */
export const updateGridRowHeightNameWrap = (options: {
  gridIdentifyClass: string;
  xuxGrid: XuxEditGridComponent | XuxEditGridCustomComponent;
  isShrink?: boolean | null;
}): void => {
  const { gridIdentifyClass, xuxGrid, isShrink } = options;

  if (!xuxGrid) {
    return;
  }

  const entryFormGridClassList = document.querySelector(
    `.widget-wrapper.${gridIdentifyClass}`,
  )?.classList;

  const isGridNameCellWrap = includes(entryFormGridClassList, 'name-cell-wrap');

  if (!isGridNameCellWrap || isShrink) {
    entryFormGridClassList?.add('name-cell-wrap');
  } else {
    entryFormGridClassList?.remove('name-cell-wrap');
  }

  xuxGrid.resetGridRowHeights();
};
/**
 * --end-- helper functions for editable ag-grid!
 */
